import React, { useState, useEffect } from 'react';
import { Analytics } from "@vercel/analytics/react";
import { UserButton, useUser } from '@clerk/clerk-react';

function EnhancedDashboard() {
    const { user } = useUser();
    const [policyChanges, setPolicyChanges] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [stats, setStats] = useState({
        totalChanges: 0,
        highImpact: 0,
        last30Days: 0,
        lastScanTime: null
    });
    const [selectedTimeframe, setSelectedTimeframe] = useState(30);
    const [selectedImpact, setSelectedImpact] = useState('all');
    const [selectedCarrier, setSelectedCarrier] = useState('all');
    const [selectedSpecialty, setSelectedSpecialty] = useState('all');
    const [expandedCard, setExpandedCard] = useState(null);
    const [selectedTimezone, setSelectedTimezone] = useState('America/Los_Angeles'); // Default to PST

    // Get API URL from environment
    const API_URL = process.env.REACT_APP_API_URL ||
        'https://n7yb9pyn91.execute-api.us-east-2.amazonaws.com/prod';

    useEffect(() => {
        fetchPolicyChanges();
    }, [selectedTimeframe]);

    // Helper function to normalize DynamoDB format
    const normalizeDynamoDBItem = (item) => {
        const normalized = {};
        for (const [key, value] of Object.entries(item)) {
            if (typeof value === 'object' && value !== null) {
                if (value.S) normalized[key] = value.S;
                else if (value.N) normalized[key] = parseFloat(value.N);
                else if (value.L) normalized[key] = value.L.map(v => v.S || v.N || v);
                else if (value.SS) normalized[key] = value.SS;
                else normalized[key] = value;
            } else {
                normalized[key] = value;
            }
        }
        return normalized;
    };

    const fetchPolicyChanges = async () => {
        setLoading(true);
        setError(null);

        try {
            const params = new URLSearchParams({
                days: selectedTimeframe.toString()
            });

            const apiUrl = `${API_URL}/policy-changes?${params.toString()}`;
            console.log('🔍 Fetching from API:', apiUrl);

            const response = await fetch(apiUrl, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error(`API request failed: ${response.status} ${response.statusText}`);
            }

            const data = await response.json();
            console.log('📊 API Response:', data);

            // Normalize DynamoDB format if needed
            const normalizedChanges = (data.policyChanges || []).map(change => {
                // Check if it's DynamoDB format (has nested objects with type indicators)
                const hasNestedFormat = Object.values(change).some(value =>
                    value && typeof value === 'object' && (value.S || value.N || value.L)
                );

                return hasNestedFormat ? normalizeDynamoDBItem(change) : change;
            });

            // Update state with normalized data
            setPolicyChanges(normalizedChanges);
            setStats(data.stats || {
                totalChanges: normalizedChanges.length,
                highImpact: normalizedChanges.filter(c => (c.impact_level || '').toUpperCase() === 'HIGH').length,
                last30Days: normalizedChanges.length,
                lastScanTime: new Date().toISOString()
            });

        } catch (err) {
            console.error('❌ Error fetching data:', err);
            setError(`Failed to fetch policy changes: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const getImpactColor = (level) => {
        switch (String(level).toUpperCase()) {
            case 'HIGH': return '#dc2626';
            case 'MEDIUM': return '#d97706';
            case 'LOW': return '#2563eb';
            default: return '#6b7280';
        }
    };

    const getChangeTypeIcon = (type) => {
        switch (String(type).toUpperCase()) {
            case 'NEW': return '🆕';
            case 'MODIFIED': return '📝';
            case 'REMOVED': return '🗑️';
            case 'AI_ANALYZED': return '🤖';
            case 'DETECTED': return '🔍';
            case 'POLICY_CHANGE': return '📋';
            default: return '📄';
        }
    };

    const formatDate = (dateString) => {
        try {
            const date = new Date(dateString);

            if (isNaN(date.getTime())) {
                return 'Unknown date';
            }

            return date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                timeZone: selectedTimezone, // Use the selected timezone
                timeZoneName: 'short'
            });
        } catch (error) {
            console.error('Error formatting date:', error);
            return 'Unknown date';
        }
    };

    const timezoneOptions = [
        { value: 'America/Los_Angeles', label: 'Pacific Time (PST/PDT)' },
        { value: 'America/Denver', label: 'Mountain Time (MST/MDT)' },
        { value: 'America/Chicago', label: 'Central Time (CST/CDT)' },
        { value: 'America/New_York', label: 'Eastern Time (EST/EDT)' },
        { value: 'UTC', label: 'UTC' }
    ];

    const formatConfidenceScore = (score) => {
        if (!score) return null;
        return Math.round(score * 100);
    };

    const formatSpecialties = (specialties) => {
        if (!specialties) return '';
        if (Array.isArray(specialties)) {
            return specialties.slice(0, 3).join(', ') + (specialties.length > 3 ? '...' : '');
        }
        return String(specialties);
    };

    // Extract unique values for filter dropdowns
    const getUniqueCarriers = () => {
        const carriers = new Set();
        policyChanges.forEach(change => {
            if (change.source) carriers.add(change.source);
            if (change.carrier) carriers.add(change.carrier);
        });
        return Array.from(carriers).sort();
    };

    const getUniqueSpecialties = () => {
        const specialties = new Set();
        policyChanges.forEach(change => {
            if (change.affected_specialties) {
                if (Array.isArray(change.affected_specialties)) {
                    change.affected_specialties.forEach(spec => specialties.add(spec));
                } else {
                    specialties.add(change.affected_specialties);
                }
            }
        });
        return Array.from(specialties).sort();
    };

    const getFilteredChanges = () => {
        return policyChanges.filter(change => {
            // Impact filter
            if (selectedImpact !== 'all' && (change.impact_level || '').toLowerCase() !== selectedImpact) {
                return false;
            }

            // Carrier filter
            if (selectedCarrier !== 'all') {
                const changeCarrier = change.source || change.carrier || '';
                if (changeCarrier !== selectedCarrier) {
                    return false;
                }
            }

            // Specialty filter
            if (selectedSpecialty !== 'all') {
                const changeSpecialties = change.affected_specialties || [];
                const specialtyArray = Array.isArray(changeSpecialties) ? changeSpecialties : [changeSpecialties];
                if (!specialtyArray.some(spec => String(spec).toLowerCase().includes(selectedSpecialty.toLowerCase()))) {
                    return false;
                }
            }

            return true;
        });
    };

    const toggleExpanded = (changeId) => {
        setExpandedCard(expandedCard === changeId ? null : changeId);
    };

    const generateClinicInsights = (change) => {
        const insights = [];

        if (change.impact_level === 'HIGH') {
            insights.push('🚨 Immediate clinic action required');
        }

        if (change.effective_date && change.effective_date !== 'Ongoing') {
            try {
                const effectiveDate = new Date(change.effective_date);
                const now = new Date();
                const daysUntil = Math.ceil((effectiveDate - now) / (1000 * 60 * 60 * 24));

                if (daysUntil <= 30 && daysUntil > 0) {
                    insights.push(`⏰ Implementation deadline: ${daysUntil} days`);
                } else if (daysUntil <= 0) {
                    insights.push('✅ Currently in effect - review compliance');
                }
            } catch (e) {
                if (change.effective_date === 'Ongoing') {
                    insights.push('🔄 Ongoing implementation required');
                }
            }
        } else if (change.effective_date === 'Ongoing') {
            insights.push('🔄 Ongoing implementation required');
        }

        if (change.affected_specialties && change.affected_specialties.length > 0) {
            insights.push('🏥 Specialty-specific requirements');
        }

        if (change.affected_providers) {
            insights.push('👨‍⚕️ Provider-specific changes');
        }

        if (change.ai_summary && (change.ai_summary.toLowerCase().includes('patient') ||
            change.ai_summary.toLowerCase().includes('member'))) {
            insights.push('👥 Direct patient impact');
        }

        if (change.confidence_score && change.confidence_score > 0.8) {
            insights.push('🎯 High confidence analysis');
        }

        // Generate insights from available fields
        if (change.key_insights && Array.isArray(change.key_insights) && change.key_insights.length > 0) {
            insights.push('💡 AI-generated key insights available');
        }

        if (change.risk_assessment) {
            insights.push('⚠️ Risk assessment completed');
        }

        if (change.action_required) {
            insights.push('✅ Specific actions identified');
        }

        return insights;
    };

    // Generate enhanced content from AI analysis
    const generateEnhancedContent = (change) => {
        const enhanced = {};

        // Generate key insights if not present
        if (!change.key_insights || change.key_insights.length === 0) {
            enhanced.key_insights = generateKeyInsights(change);
        }

        // Generate risk assessment if not present
        if (!change.risk_assessment) {
            enhanced.risk_assessment = generateRiskAssessment(change);
        }

        // Generate action items if not present
        if (!change.action_required) {
            enhanced.action_required = generateActionItems(change);
        }

        return enhanced;
    };

    const generateKeyInsights = (change) => {
        const insights = [];
        const content = (change.ai_summary || change.summary || change.content || '').toLowerCase();

        if (content.includes('prior authorization') || content.includes('precertification')) {
            insights.push('Prior authorization requirements may affect patient care workflows');
        }

        if (content.includes('reimbursement') || content.includes('payment')) {
            insights.push('Revenue cycle and billing processes may be impacted');
        }

        if (content.includes('deadline') || content.includes('effective')) {
            insights.push('Time-sensitive implementation required for compliance');
        }

        if (content.includes('provider') || content.includes('clinic')) {
            insights.push('Direct operational impact on clinic procedures');
        }

        if (content.includes('patient') || content.includes('member')) {
            insights.push('Changes affect patient experience and care delivery');
        }

        if (change.impact_level === 'HIGH') {
            insights.push('High-priority change requiring immediate management attention');
        }

        return insights.length > 0 ? insights : ['Review policy change for operational impact on your clinic'];
    };

    const generateRiskAssessment = (change) => {
        const content = (change.ai_summary || change.summary || change.content || '').toLowerCase();

        if (change.impact_level === 'HIGH') {
            if (content.includes('prior authorization') || content.includes('reimbursement')) {
                return 'HIGH RISK: This change could significantly impact clinic revenue and patient access to care. Immediate staff training and workflow updates required.';
            }
            return 'HIGH RISK: Significant operational impact expected. Review and update clinic procedures immediately.';
        }

        if (change.impact_level === 'MEDIUM') {
            return 'MEDIUM RISK: Moderate operational impact. Plan implementation within 30 days and inform relevant staff.';
        }

        return 'LOW RISK: Minor impact expected. Monitor for compliance and update procedures as needed.';
    };

    const generateActionItems = (change) => {
        const content = (change.ai_summary || change.summary || change.content || '').toLowerCase();
        const actions = [];

        // Review and assessment
        actions.push('1. Review complete policy document at source link');
        actions.push('2. Assess impact on current clinic workflows');

        // Specific actions based on content
        if (content.includes('prior authorization') || content.includes('precertification')) {
            actions.push('3. Update prior authorization workflows and train staff');
            actions.push('4. Verify authorization requirements in practice management system');
        }

        if (content.includes('billing') || content.includes('coding')) {
            actions.push('3. Update billing procedures and coding guidelines');
            actions.push('4. Train billing staff on new requirements');
        }

        if (content.includes('reimbursement') || content.includes('payment')) {
            actions.push('3. Review reimbursement impact with billing department');
            actions.push('4. Update financial projections if needed');
        }

        // Standard actions
        if (change.effective_date && change.effective_date !== 'Ongoing') {
            actions.push('5. Schedule implementation before effective date');
        } else {
            actions.push('5. Implement changes as soon as possible');
        }

        actions.push('6. Communicate changes to relevant clinical and administrative staff');
        actions.push('7. Monitor compliance and adjust procedures as needed');

        return actions.join('\n');
    };

    const clearAllFilters = () => {
        setSelectedImpact('all');
        setSelectedCarrier('all');
        setSelectedSpecialty('all');
    };

    const filteredChanges = getFilteredChanges();
    const uniqueCarriers = getUniqueCarriers();
    const uniqueSpecialties = getUniqueSpecialties();

    return (
        <div style={{
            minHeight: '100vh',
            backgroundColor: '#f9fafb',
            fontFamily: 'Arial, sans-serif'
        }}>
            {/* Header */}
            <div style={{
                backgroundColor: 'white',
                borderBottom: '1px solid #e5e7eb',
                padding: '16px 24px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ fontSize: '1.5rem', marginRight: '8px' }}>🏥</span>
                    <div>
                        <h1 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 'bold' }}>
                            Healthcare Policy Monitor
                        </h1>
                        <p style={{ margin: 0, fontSize: '0.875rem', color: '#6b7280' }}>
                            Clinic-Focused Policy Intelligence with AI Analysis
                        </p>
                    </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {/* User Welcome Message */}
                    {user && (
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            marginRight: '12px'
                        }}>
                            <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                                Welcome, {user.firstName || user.emailAddresses[0].emailAddress}!
                            </span>
                        </div>
                    )}

                    {/* Status Badges */}
                    <div style={{
                        backgroundColor: '#f3e8ff',
                        color: '#7c3aed',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        fontSize: '0.875rem',
                        fontWeight: '500'
                    }}>
                        📊 Analytics Active
                    </div>
                    <div style={{
                        backgroundColor: '#ecfdf5',
                        color: '#065f46',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        fontSize: '0.875rem',
                        fontWeight: '500'
                    }}>
                        🔐 Authenticated
                    </div>

                    {/* User Menu Button */}
                    <UserButton
                        afterSignOutUrl="/"
                        appearance={{
                            elements: {
                                avatarBox: {
                                    height: '32px',
                                    width: '32px'
                                }
                            }
                        }}
                    />
                </div>
            </div>

            <div style={{
                maxWidth: '1200px',
                margin: '0 auto',
                padding: '24px 16px'
            }}>
                {/* Stats Overview */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                    gap: '20px',
                    marginBottom: '32px'
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        padding: '24px',
                        borderRadius: '8px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                        textAlign: 'center'
                    }}>
                        <div style={{
                            fontSize: '2.5rem',
                            fontWeight: 'bold',
                            color: '#2563eb',
                            marginBottom: '8px'
                        }}>
                            {filteredChanges.length}
                        </div>
                        <div style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                            {selectedImpact !== 'all' || selectedCarrier !== 'all' || selectedSpecialty !== 'all'
                                ? 'Filtered Policy Changes'
                                : 'Total Policy Changes'}
                        </div>
                    </div>

                    <div style={{
                        backgroundColor: 'white',
                        padding: '24px',
                        borderRadius: '8px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                        textAlign: 'center'
                    }}>
                        <div style={{
                            fontSize: '2.5rem',
                            fontWeight: 'bold',
                            color: '#dc2626',
                            marginBottom: '8px'
                        }}>
                            {filteredChanges.filter(c => (c.impact_level || '').toUpperCase() === 'HIGH').length}
                        </div>
                        <div style={{ color: '#6b7280', fontSize: '0.875rem' }}>High Impact for Clinics</div>
                    </div>

                    <div style={{
                        backgroundColor: 'white',
                        padding: '24px',
                        borderRadius: '8px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                        textAlign: 'center'
                    }}>
                        <div style={{
                            fontSize: '2.5rem',
                            fontWeight: 'bold',
                            color: '#16a34a',
                            marginBottom: '8px'
                        }}>
                            {uniqueCarriers.length}
                        </div>
                        <div style={{ color: '#6b7280', fontSize: '0.875rem' }}>Insurance Carriers</div>
                    </div>

                    <div style={{
                        backgroundColor: 'white',
                        padding: '24px',
                        borderRadius: '8px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                        textAlign: 'center'
                    }}>
                        <div style={{
                            fontSize: '2.5rem',
                            fontWeight: 'bold',
                            color: '#d97706',
                            marginBottom: '8px'
                        }}>
                            {uniqueSpecialties.length}
                        </div>
                        <div style={{ color: '#6b7280', fontSize: '0.875rem' }}>Affected Specialties</div>
                    </div>
                </div>

                {/* Enhanced Filtering Controls */}
                <div style={{
                    backgroundColor: 'white',
                    padding: '24px',
                    borderRadius: '8px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    marginBottom: '24px'
                }}>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        marginBottom: '20px'
                    }}>
                        <div>
                            <h2 style={{ margin: '0 0 8px 0', fontSize: '1.25rem', fontWeight: 'bold' }}>
                                Clinic-Focused Policy Changes
                            </h2>
                            <p style={{ margin: 0, fontSize: '0.875rem', color: '#6b7280' }}>
                                Last scan: {stats.lastScanTime ? formatDate(stats.lastScanTime) : 'Loading...'} •
                                AI-powered policy analysis with automated monitoring
                            </p>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <button
                                onClick={() => fetchPolicyChanges(false)}
                                disabled={loading}
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: '#2563eb',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    fontSize: '0.875rem',
                                    cursor: loading ? 'not-allowed' : 'pointer',
                                    opacity: loading ? 0.6 : 1
                                }}
                            >
                                {loading ? 'Loading...' : '🔄 Refresh'}
                            </button>
                        </div>
                    </div>

                    {/* Filter Controls */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                        gap: '16px',
                        padding: '20px',
                        backgroundColor: '#f8fafc',
                        borderRadius: '8px',
                        border: '1px solid #e2e8f0'
                    }}>
                        {/* Timeframe Filter */}
                        <div>
                            <label style={{
                                display: 'block',
                                fontSize: '0.875rem',
                                color: '#374151',
                                fontWeight: '600',
                                marginBottom: '6px'
                            }}>
                                📅 Timeframe
                            </label>
                            <select
                                value={selectedTimeframe}
                                onChange={(e) => setSelectedTimeframe(Number(e.target.value))}
                                style={{
                                    width: '100%',
                                    padding: '8px 12px',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '6px',
                                    fontSize: '0.875rem',
                                    backgroundColor: 'white'
                                }}
                            >
                                <option value={7}>Last 7 days</option>
                                <option value={30}>Last 30 days</option>
                                <option value={90}>Last 90 days</option>
                            </select>
                        </div>

                        {/* Impact Filter */}
                        <div>
                            <label style={{
                                display: 'block',
                                fontSize: '0.875rem',
                                color: '#374151',
                                fontWeight: '600',
                                marginBottom: '6px'
                            }}>
                                ⚠️ Impact Level
                            </label>
                            <select
                                value={selectedImpact}
                                onChange={(e) => setSelectedImpact(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '8px 12px',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '6px',
                                    fontSize: '0.875rem',
                                    backgroundColor: 'white'
                                }}
                            >
                                <option value="all">All Impact Levels</option>
                                <option value="high">High Impact Only</option>
                                <option value="medium">Medium Impact Only</option>
                                <option value="low">Low Impact Only</option>
                            </select>
                        </div>

                        {/* Insurance Carrier Filter */}
                        <div>
                            <label style={{
                                display: 'block',
                                fontSize: '0.875rem',
                                color: '#374151',
                                fontWeight: '600',
                                marginBottom: '6px'
                            }}>
                                🏢 Insurance Carrier
                            </label>
                            <select
                                value={selectedCarrier}
                                onChange={(e) => setSelectedCarrier(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '8px 12px',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '6px',
                                    fontSize: '0.875rem',
                                    backgroundColor: 'white'
                                }}
                            >
                                <option value="all">All Carriers</option>
                                {uniqueCarriers.map(carrier => (
                                    <option key={carrier} value={carrier}>{carrier}</option>
                                ))}
                            </select>
                        </div>

                        {/* Specialty Filter */}
                        <div>
                            <label style={{
                                display: 'block',
                                fontSize: '0.875rem',
                                color: '#374151',
                                fontWeight: '600',
                                marginBottom: '6px'
                            }}>
                                🩺 Practice Specialty
                            </label>
                            <select
                                value={selectedSpecialty}
                                onChange={(e) => setSelectedSpecialty(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '8px 12px',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '6px',
                                    fontSize: '0.875rem',
                                    backgroundColor: 'white'
                                }}
                            >
                                <option value="all">All Specialties</option>
                                {uniqueSpecialties.map(specialty => (
                                    <option key={specialty} value={specialty}>{specialty}</option>
                                ))}
                            </select>
                        </div>

                        {/* Timezone Selector */}
                        <div>
                            <label style={{
                                display: 'block',
                                fontSize: '0.875rem',
                                color: '#374151',
                                fontWeight: '600',
                                marginBottom: '6px'
                            }}>
                                🌐 Timezone Display
                            </label>
                            <select
                                value={selectedTimezone}
                                onChange={(e) => setSelectedTimezone(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '8px 12px',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '6px',
                                    fontSize: '0.875rem',
                                    backgroundColor: 'white'
                                }}
                            >
                                {timezoneOptions.map(option => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Clear Filters Button */}
                        <div style={{ display: 'flex', alignItems: 'end' }}>
                            <button
                                onClick={clearAllFilters}
                                style={{
                                    width: '100%',
                                    padding: '8px 12px',
                                    backgroundColor: '#6b7280',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    fontSize: '0.875rem',
                                    cursor: 'pointer',
                                    fontWeight: '500'
                                }}
                            >
                                🔄 Clear All Filters
                            </button>
                        </div>
                    </div>

                    {/* Active Filters Display */}
                    {(selectedImpact !== 'all' || selectedCarrier !== 'all' || selectedSpecialty !== 'all') && (
                        <div style={{
                            marginTop: '16px',
                            padding: '12px',
                            backgroundColor: '#eff6ff',
                            borderRadius: '6px',
                            border: '1px solid #bfdbfe'
                        }}>
                            <div style={{ fontSize: '0.875rem', color: '#1e40af', fontWeight: '500' }}>
                                🔍 Active Filters:
                                {selectedImpact !== 'all' && <span style={{ marginLeft: '8px', padding: '2px 6px', backgroundColor: '#dbeafe', borderRadius: '4px' }}>Impact: {selectedImpact}</span>}
                                {selectedCarrier !== 'all' && <span style={{ marginLeft: '8px', padding: '2px 6px', backgroundColor: '#dbeafe', borderRadius: '4px' }}>Carrier: {selectedCarrier}</span>}
                                {selectedSpecialty !== 'all' && <span style={{ marginLeft: '8px', padding: '2px 6px', backgroundColor: '#dbeafe', borderRadius: '4px' }}>Specialty: {selectedSpecialty}</span>}
                            </div>
                        </div>
                    )}
                </div>

                {/* Policy Changes List */}
                <div style={{
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    overflow: 'hidden'
                }}>
                    {loading ? (
                        <div style={{ padding: '60px', textAlign: 'center' }}>
                            <div style={{ fontSize: '2rem', marginBottom: '16px' }}>🔄</div>
                            <p style={{ color: '#6b7280' }}>Loading clinic-focused policy changes...</p>
                        </div>
                    ) : error ? (
                        <div style={{
                            padding: '40px',
                            textAlign: 'center',
                            backgroundColor: '#fef2f2',
                            color: '#dc2626'
                        }}>
                            <div style={{ fontSize: '2rem', marginBottom: '16px' }}>⚠️</div>
                            <p>{error}</p>
                            <button
                                onClick={() => fetchPolicyChanges(false)}
                                style={{
                                    marginTop: '16px',
                                    padding: '8px 16px',
                                    backgroundColor: '#dc2626',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer'
                                }}
                            >
                                Retry
                            </button>
                        </div>
                    ) : filteredChanges.length === 0 ? (
                        <div style={{ padding: '60px', textAlign: 'center' }}>
                            <div style={{ fontSize: '2rem', marginBottom: '16px' }}>📄</div>
                            <p style={{ color: '#6b7280' }}>
                                No policy changes found matching your criteria
                            </p>
                            <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                                Try adjusting your filters or use the "🤖 Force AI Scan" button to check for new updates.
                            </p>
                        </div>
                    ) : (
                        <div>
                            {filteredChanges.map((change, index) => {
                                const isExpanded = expandedCard === (change.id || change.change_id || index);
                                const clinicInsights = generateClinicInsights(change);

                                return (
                                    <div
                                        key={change.id || change.change_id || index}
                                        style={{
                                            borderBottom: index < filteredChanges.length - 1 ? '1px solid #e5e7eb' : 'none'
                                        }}
                                    >
                                        {/* Main Card Content */}
                                        <div
                                            style={{
                                                padding: '24px',
                                                cursor: 'pointer',
                                                transition: 'background-color 0.2s ease'
                                            }}
                                            onClick={() => toggleExpanded(change.id || change.change_id || index)}
                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                                        >
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                <div style={{ flex: 1 }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                                                        <span style={{ fontSize: '1.25rem' }}>
                                                            {getChangeTypeIcon(change.change_type || change.type)}
                                                        </span>
                                                        <span style={{
                                                            fontSize: '0.75rem',
                                                            fontWeight: 'bold',
                                                            padding: '4px 8px',
                                                            borderRadius: '4px',
                                                            backgroundColor: '#f3f4f6',
                                                            color: '#374151'
                                                        }}>
                                                            {change.source || 'Unknown Source'}
                                                        </span>
                                                        <span style={{
                                                            fontSize: '0.75rem',
                                                            fontWeight: 'bold',
                                                            padding: '4px 8px',
                                                            borderRadius: '4px',
                                                            backgroundColor: (change.impact_level || '').toUpperCase() === 'HIGH' ? '#fef2f2' :
                                                                (change.impact_level || '').toUpperCase() === 'MEDIUM' ? '#fef3c7' : '#eff6ff',
                                                            color: getImpactColor(change.impact_level || 'LOW')
                                                        }}>
                                                            {(change.impact_level || 'UNKNOWN').toUpperCase()} IMPACT
                                                        </span>
                                                        {/* AI Analysis Badge */}
                                                        {(change.ai_summary || change.claude_summary || change.ai_analysis) && (
                                                            <span style={{
                                                                fontSize: '0.75rem',
                                                                fontWeight: 'bold',
                                                                padding: '4px 8px',
                                                                borderRadius: '4px',
                                                                backgroundColor: '#f0f9ff',
                                                                color: '#0369a1'
                                                            }}>
                                                                🤖 AI ANALYZED
                                                            </span>
                                                        )}
                                                        {/* Confidence Score Badge */}
                                                        {change.confidence_score && (
                                                            <span style={{
                                                                fontSize: '0.75rem',
                                                                fontWeight: 'bold',
                                                                padding: '4px 8px',
                                                                borderRadius: '4px',
                                                                backgroundColor: '#f0fdf4',
                                                                color: '#166534'
                                                            }}>
                                                                🎯 {formatConfidenceScore(change.confidence_score)}%
                                                            </span>
                                                        )}
                                                    </div>

                                                    <h3 style={{
                                                        margin: '0 0 8px 0',
                                                        fontSize: '1.125rem',
                                                        fontWeight: 'bold',
                                                        color: '#111827'
                                                    }}>
                                                        {change.title || change.policy_title || 'Policy Change'}
                                                    </h3>

                                                    {/* Clinic Insights */}
                                                    {clinicInsights.length > 0 && (
                                                        <div style={{
                                                            display: 'flex',
                                                            gap: '8px',
                                                            marginBottom: '12px',
                                                            flexWrap: 'wrap'
                                                        }}>
                                                            {clinicInsights.map((insight, idx) => (
                                                                <span
                                                                    key={idx}
                                                                    style={{
                                                                        fontSize: '0.75rem',
                                                                        fontWeight: '600',
                                                                        padding: '4px 8px',
                                                                        borderRadius: '4px',
                                                                        backgroundColor: '#fef3c7',
                                                                        color: '#92400e',
                                                                        border: '1px solid #fde68a'
                                                                    }}
                                                                >
                                                                    {insight}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    )}

                                                    {/* Summary */}
                                                    <p style={{
                                                        margin: '0 0 12px 0',
                                                        color: '#6b7280',
                                                        lineHeight: '1.5'
                                                    }}>
                                                        {change.ai_summary || change.claude_summary || change.ai_analysis ||
                                                            change.summary || change.description || 'No description available'}
                                                    </p>

                                                    <div style={{
                                                        display: 'flex',
                                                        flexWrap: 'wrap',
                                                        gap: '16px',
                                                        fontSize: '0.875rem',
                                                        color: '#6b7280'
                                                    }}>
                                                        <div>📅 Detected: {formatDate(change.date_detected || change.timestamp || change.created_at)}</div>
                                                        {change.effective_date && (
                                                            <div>⚡ Effective: {formatDate(change.effective_date)}</div>
                                                        )}
                                                        {change.affected_specialties && change.affected_specialties.length > 0 && (
                                                            <div>🏥 Affects: {formatSpecialties(change.affected_specialties)}</div>
                                                        )}
                                                        {change.change_type && (
                                                            <div>🔄 Type: {change.change_type}</div>
                                                        )}
                                                    </div>

                                                    <div style={{
                                                        marginTop: '12px',
                                                        fontSize: '0.875rem',
                                                        color: '#9ca3af',
                                                        fontStyle: 'italic'
                                                    }}>
                                                        💡 Click to {isExpanded ? 'collapse' : 'expand'} for detailed clinic insights and action items
                                                    </div>
                                                </div>

                                                <div style={{ marginLeft: '16px' }}>
                                                    <a
                                                        href={change.url || change.source_url || '#'}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        onClick={(e) => e.stopPropagation()}
                                                        style={{
                                                            padding: '8px 16px',
                                                            backgroundColor: '#2563eb',
                                                            color: 'white',
                                                            textDecoration: 'none',
                                                            borderRadius: '6px',
                                                            fontSize: '0.875rem',
                                                            fontWeight: '500'
                                                        }}
                                                    >
                                                        View Source
                                                    </a>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Expanded Content */}
                                        {isExpanded && (
                                            <div style={{
                                                padding: '24px',
                                                backgroundColor: '#f8fafc',
                                                borderTop: '1px solid #e5e7eb'
                                            }}>
                                                {(() => {
                                                    // Generate enhanced content if missing
                                                    const enhanced = generateEnhancedContent(change);
                                                    const keyInsights = change.key_insights || enhanced.key_insights;
                                                    const riskAssessment = change.risk_assessment || enhanced.risk_assessment;
                                                    const actionRequired = change.action_required || enhanced.action_required;

                                                    return (
                                                        <>
                                                            {/* Key Insights for Clinics */}
                                                            {keyInsights && keyInsights.length > 0 && (
                                                                <div style={{
                                                                    backgroundColor: '#fefce8',
                                                                    border: '1px solid #fde68a',
                                                                    borderRadius: '8px',
                                                                    padding: '16px',
                                                                    marginBottom: '16px'
                                                                }}>
                                                                    <div style={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        gap: '8px',
                                                                        marginBottom: '8px'
                                                                    }}>
                                                                        <span style={{ fontSize: '1rem' }}>💡</span>
                                                                        <span style={{
                                                                            fontSize: '0.875rem',
                                                                            fontWeight: 'bold',
                                                                            color: '#92400e'
                                                                        }}>
                                                                            Key Insights for Your Clinic
                                                                        </span>
                                                                        {!change.key_insights && (
                                                                            <span style={{
                                                                                fontSize: '0.75rem',
                                                                                color: '#92400e',
                                                                                backgroundColor: '#fef3c7',
                                                                                padding: '2px 6px',
                                                                                borderRadius: '4px',
                                                                                marginLeft: 'auto'
                                                                            }}>
                                                                                AI-Generated
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                    <div style={{
                                                                        color: '#78350f',
                                                                        lineHeight: '1.6',
                                                                        fontSize: '0.9rem'
                                                                    }}>
                                                                        {Array.isArray(keyInsights) ? (
                                                                            <ul style={{ margin: 0, paddingLeft: '20px' }}>
                                                                                {keyInsights.map((insight, idx) => (
                                                                                    <li key={idx} style={{ marginBottom: '4px' }}>{insight}</li>
                                                                                ))}
                                                                            </ul>
                                                                        ) : (
                                                                            <p style={{ margin: 0 }}>{keyInsights}</p>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            )}

                                                            {/* Risk Assessment for Patient Care */}
                                                            {riskAssessment && (
                                                                <div style={{
                                                                    backgroundColor: '#fef2f2',
                                                                    border: '1px solid #fecaca',
                                                                    borderRadius: '8px',
                                                                    padding: '16px',
                                                                    marginBottom: '16px'
                                                                }}>
                                                                    <div style={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        gap: '8px',
                                                                        marginBottom: '8px'
                                                                    }}>
                                                                        <span style={{ fontSize: '1rem' }}>⚠️</span>
                                                                        <span style={{
                                                                            fontSize: '0.875rem',
                                                                            fontWeight: 'bold',
                                                                            color: '#dc2626'
                                                                        }}>
                                                                            Patient Care Risk Assessment
                                                                        </span>
                                                                        {!change.risk_assessment && (
                                                                            <span style={{
                                                                                fontSize: '0.75rem',
                                                                                color: '#dc2626',
                                                                                backgroundColor: '#fecaca',
                                                                                padding: '2px 6px',
                                                                                borderRadius: '4px',
                                                                                marginLeft: 'auto'
                                                                            }}>
                                                                                AI-Generated
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                    <p style={{
                                                                        margin: 0,
                                                                        color: '#991b1b',
                                                                        lineHeight: '1.6',
                                                                        fontSize: '0.9rem'
                                                                    }}>
                                                                        {riskAssessment}
                                                                    </p>
                                                                </div>
                                                            )}

                                                            {/* Clinic Action Items */}
                                                            {actionRequired && (
                                                                <div style={{
                                                                    backgroundColor: '#eff6ff',
                                                                    border: '1px solid #bfdbfe',
                                                                    borderRadius: '8px',
                                                                    padding: '16px',
                                                                    marginBottom: '16px'
                                                                }}>
                                                                    <div style={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        gap: '8px',
                                                                        marginBottom: '8px'
                                                                    }}>
                                                                        <span style={{ fontSize: '1rem' }}>✅</span>
                                                                        <span style={{
                                                                            fontSize: '0.875rem',
                                                                            fontWeight: 'bold',
                                                                            color: '#1d4ed8'
                                                                        }}>
                                                                            Recommended Clinic Actions
                                                                        </span>
                                                                        {!change.action_required && (
                                                                            <span style={{
                                                                                fontSize: '0.75rem',
                                                                                color: '#1d4ed8',
                                                                                backgroundColor: '#dbeafe',
                                                                                padding: '2px 6px',
                                                                                borderRadius: '4px',
                                                                                marginLeft: 'auto'
                                                                            }}>
                                                                                AI-Generated
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                    <div style={{
                                                                        margin: 0,
                                                                        color: '#1e40af',
                                                                        lineHeight: '1.6',
                                                                        fontSize: '0.9rem',
                                                                        whiteSpace: 'pre-line'
                                                                    }}>
                                                                        {actionRequired}
                                                                    </div>
                                                                </div>
                                                            )}

                                                            {/* Affected Specialties Detail */}
                                                            {change.affected_specialties && change.affected_specialties.length > 0 && (
                                                                <div style={{
                                                                    backgroundColor: '#f0f9ff',
                                                                    border: '1px solid #bae6fd',
                                                                    borderRadius: '8px',
                                                                    padding: '16px'
                                                                }}>
                                                                    <div style={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        gap: '8px',
                                                                        marginBottom: '8px'
                                                                    }}>
                                                                        <span style={{ fontSize: '1rem' }}>🩺</span>
                                                                        <span style={{
                                                                            fontSize: '0.875rem',
                                                                            fontWeight: 'bold',
                                                                            color: '#0369a1'
                                                                        }}>
                                                                            Specialty-Specific Impact
                                                                        </span>
                                                                    </div>
                                                                    <p style={{
                                                                        margin: 0,
                                                                        color: '#075985',
                                                                        lineHeight: '1.6',
                                                                        fontSize: '0.9rem'
                                                                    }}>
                                                                        <strong>Affected Specialties:</strong> {formatSpecialties(change.affected_specialties)}
                                                                        <br />
                                                                        <em>Ensure your clinic staff and providers are aware of these specialty-specific requirements.</em>
                                                                    </p>
                                                                </div>
                                                            )}
                                                        </>
                                                    );
                                                })()}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Enhanced Footer */}
                {!loading && !error && (
                    <div style={{
                        marginTop: '24px',
                        padding: '20px',
                        backgroundColor: '#ecfdf5',
                        borderRadius: '8px',
                        border: '1px solid #10b981'
                    }}>
                        <h3 style={{ margin: '0 0 8px 0', color: '#065f46' }}>
                            ✅ Clinic-Focused AI Policy Intelligence Active!
                        </h3>
                        <p style={{ margin: 0, color: '#065f46', fontSize: '0.875rem' }}>
                            This dashboard provides targeted policy insights for healthcare clinics, filtering by insurance carriers and practice specialties
                            to help you deliver the best patient care. AI analysis includes clinic-specific risk assessments and actionable recommendations.
                            {user && (
                                <span style={{ display: 'block', marginTop: '8px', fontWeight: '500' }}>
                                    🔐 Securely authenticated as {user.firstName || user.emailAddresses[0].emailAddress}
                                </span>
                            )}
                        </p>
                    </div>
                )}
            </div>

            {/* Vercel Analytics Component */}
            <Analytics />
        </div>
    );
}

export default EnhancedDashboard;